import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Container } from '../components/ui/Container';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { helpArticles } from '../data/helpArticles';
import { ChevronRight, Clock, Eye, Calendar, ArrowLeft, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { getTotalViews, incrementViewCount } from '../utils/viewCounts';
export function HelpArticlePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const article = helpArticles.find(a => a.slug === slug);
  const [viewCount, setViewCount] = useState(article?.views || 0);
  const relatedArticles = article?.relatedArticles ? helpArticles.filter(a => article.relatedArticles?.includes(a.id)) : [];
  useEffect(() => {
    if (!article) {
      return;
    }
    window.scrollTo(0, 0);
    // Increment view count on page load
    incrementViewCount(article.id);
    setViewCount(getTotalViews(article.id, article.views));
  }, [article]);
  if (!article) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50" data-id="element-1707">
        <div className="text-center" data-id="element-1708">
          <h1 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1709">
            Article Not Found
          </h1>
          <Link to="/help" data-id="element-1710">
            <Button data-id="element-1711">Return to Help Center</Button>
          </Link>
        </div>
      </div>;
  }
  // Generate structured data for the article
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author
    },
    datePublished: article.publishedDate,
    dateModified: article.lastUpdated,
    publisher: {
      '@type': 'Organization',
      name: 'Flowsense',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.useflowsense.com/favicon.svg'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.useflowsense.com/help/${article.slug}`
    },
    keywords: article.tags.join(', '),
    articleSection: article.category,
    wordCount: article.content.split(/\s+/).length
  };
  const Icon = article.icon;
  // Extract h2 headings from content for table of contents
  const headings = article.content.match(/^## (.*$)/gim) || [];
  const tableOfContents = headings.map(heading => {
    const text = heading.replace('## ', '');
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return {
      text,
      id
    };
  });
  // Process content to add IDs to h2 headings
  const processedContent = article.content.replace(/^## (.*$)/gim, (match, heading) => {
    const id = heading.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return `<h2 id="${id}" class="text-2xl font-bold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-100 scroll-mt-24">${heading}</h2>`;
  }).replace(/^### (.*$)/gim, '<h3 class="text-xl font-bold text-gray-900 mt-8 mb-4">$1</h3>').replace(/^#### (.*$)/gim, '<h4 class="text-lg font-semibold text-gray-900 mt-6 mb-3">$1</h4>').replace(/\*\*(.*?)\*\*/gim, '<strong class="font-semibold text-gray-900">$1</strong>').replace(/^\- (.*$)/gim, '<li class="ml-4 list-disc text-gray-600 mb-2">$1</li>').replace(/^\d\. (.*$)/gim, '<li class="ml-4 list-decimal text-gray-600 mb-2">$1</li>').replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-indigo-600 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>').replace(/\n/gim, '<br />');
  return <>
      <SEO title={`${article.title} | Flowsense`} description={article.excerpt} canonical={`https://www.useflowsense.com/help/${article.slug}`} ogType="article" structuredData={structuredData} data-id="element-1712" />
      <div className="bg-white min-h-screen" data-id="element-1713">
        {/* Breadcrumb & Header */}
        <div className="bg-gray-50 border-b border-gray-200" data-id="element-1714">
          <Container className="py-12" data-id="element-1715">
            <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-1716">
              <Link to="/help" className="hover:text-indigo-600 transition-colors" data-id="element-1717">
                Help Center
              </Link>
              <ChevronRight className="h-4 w-4 mx-2" data-id="element-1718" />
              <span className="text-gray-900 font-medium" data-id="element-1719">
                {article.category}
              </span>
            </div>

            <div className="max-w-4xl" data-id="element-1720">
              <div className="flex items-center gap-3 mb-6" data-id="element-1721">
                <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border-0" data-id="element-1722">
                  {article.category}
                </Badge>
                <span className="text-gray-400" data-id="element-1723">|</span>
                <span className="text-sm text-gray-500 flex items-center" data-id="element-1724">
                  <Calendar className="h-4 w-4 mr-1.5" data-id="element-1725" />
                  Last updated:{' '}
                  {new Date(article.lastUpdated).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1726">
                {article.title}
              </h1>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1727">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-6 text-sm text-gray-500 border-t border-gray-200 pt-6" data-id="element-1728">
                <div className="flex items-center" data-id="element-1729">
                  <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold mr-3" data-id="element-1730">
                    {article.author.charAt(0)}
                  </div>
                  <span data-id="element-1731">
                    Written by{' '}
                    <span className="font-medium text-gray-900" data-id="element-1732">
                      {article.author}
                    </span>
                  </span>
                </div>
                <div className="flex items-center" data-id="element-1733">
                  <Clock className="h-4 w-4 mr-1.5" data-id="element-1734" />
                  {article.readTime}
                </div>
                <div className="flex items-center" data-id="element-1735">
                  <Eye className="h-4 w-4 mr-1.5" data-id="element-1736" />
                  {viewCount.toLocaleString()} views
                </div>
              </div>
            </div>
          </Container>
        </div>

        <Container className="py-16" data-id="element-1737">
          <div className="grid lg:grid-cols-12 gap-12" data-id="element-1738">
            {/* Main Content */}
            <div className="lg:col-span-8" data-id="element-1739">
              <article className="prose prose-lg prose-indigo max-w-none" data-id="element-1740">
                {/* We're using a simple markdown renderer here. In production, you'd want a more robust one */}
                <div dangerouslySetInnerHTML={{
                __html: processedContent
              }} data-id="element-1741" />
              </article>

              {/* FAQs Section */}
              {article.faqs && article.faqs.length > 0 && <div className="mt-16 pt-12 border-t border-gray-200" data-id="element-1742">
                  <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-1743">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-6" data-id="element-1744">
                    {article.faqs.map((faq, index) => <div key={index} className="bg-gray-50 rounded-xl p-6" data-id="element-1745">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1746">
                          {faq.question}
                        </h3>
                        <p className="text-gray-600" data-id="element-1747">{faq.answer}</p>
                      </div>)}
                  </div>
                </div>}

              {/* Related Articles - Moved below FAQs */}
              {relatedArticles.length > 0 && <div className="mt-16 pt-12 border-t border-gray-200" data-id="element-1748">
                  <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-1749">
                    Related Articles
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6" data-id="element-1750">
                    {relatedArticles.map(related => {
                  const RelatedIcon = related.icon;
                  return <Link key={related.id} to={`/help/${related.slug}`} className="block group" data-id="element-1751">
                          <div className="bg-gray-50 rounded-xl p-6 hover:bg-indigo-50 hover:shadow-md transition-all" data-id="element-1752">
                            <div className="flex items-start gap-4" data-id="element-1753">
                              <div className="flex-shrink-0" data-id="element-1754">
                                <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors" data-id="element-1755">
                                  <RelatedIcon className="h-5 w-5" data-id="element-1756" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-1757">
                                <h3 className="font-semibold text-gray-900 group-hover:text-indigo-700 mb-2 line-clamp-2" data-id="element-1758">
                                  {related.title}
                                </h3>
                                <p className="text-sm text-gray-600 mb-3 line-clamp-2" data-id="element-1759">
                                  {related.excerpt}
                                </p>
                                <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-1760">
                                  <span className="flex items-center" data-id="element-1761">
                                    <Clock className="h-3 w-3 mr-1" data-id="element-1762" />
                                    {related.readTime}
                                  </span>
                                  <span className="flex items-center" data-id="element-1763">
                                    <Eye className="h-3 w-3 mr-1" data-id="element-1764" />
                                    {getTotalViews(related.id, related.views).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>;
                })}
                  </div>
                </div>}

              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-gray-200" data-id="element-1765">
                <div className="flex flex-wrap gap-2" data-id="element-1766">
                  {article.tags.map(tag => <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600" data-id="element-1767">
                      <Tag className="h-3 w-3 mr-1.5" data-id="element-1768" />
                      {tag}
                    </span>)}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 space-y-8" data-id="element-1769">
              {/* Table of Contents - Simplified for this demo */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-24" data-id="element-1770">
                <h3 className="font-bold text-gray-900 mb-4" data-id="element-1771">
                  In this article
                </h3>
                <ul className="space-y-3 text-sm" data-id="element-1772">
                  {tableOfContents.map((item, i) => <li key={i} data-id="element-1773">
                      <a href={`#${item.id}`} className="text-gray-600 hover:text-indigo-600 transition-colors block py-1" onClick={e => {
                    e.preventDefault();
                    const element = document.getElementById(item.id);
                    if (element) {
                      element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }
                  }} data-id="element-1774">
                        {item.text}
                      </a>
                    </li>)}
                </ul>

                <div className="mt-8 pt-8 border-t border-gray-100" data-id="element-1775">
                  <h3 className="font-bold text-gray-900 mb-4" data-id="element-1776">
                    Need more help?
                  </h3>
                  <Link to="/contact" data-id="element-1777">
                    <Button className="w-full" data-id="element-1778">Contact Support</Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </>;
}